import React from 'react';
import { BottomSheet } from './BottomSheet';

export interface ActionSheetAction {
  icon?: React.ReactNode;
  label: string;
  /** Extra description below the label */
  subtitle?: string;
  onClick: () => void;
  variant?: 'default' | 'danger';
  disabled?: boolean;
}

interface ActionSheetProps {
  isOpen: boolean;
  onClose: () => void;
  actions: ActionSheetAction[];
  title?: string;
  cancelLabel?: string;
}

/**
 * iOS-style action sheet with grouped actions and a separate cancel button.
 *
 * Pattern: Rounded card with dividers between actions, plus a standalone
 * Cancel button card below — identical to iOS UIActionSheet / Todoist.
 */
export function ActionSheet({
  isOpen,
  onClose,
  actions,
  title,
  cancelLabel = 'Avbryt',
}: ActionSheetProps) {
  return (
    <BottomSheet isOpen={isOpen} onClose={onClose} variant="sheet" title={title} showHandle={false}>
      <div className="px-3 pb-3 pt-1 sm:px-4 sm:pb-4 sm:pt-2">
        {/* Action group */}
        <div className="bg-[var(--bg-surface-alt)] rounded-ios-card overflow-hidden">
          {actions.map((action, i) => (
            <React.Fragment key={i}>
              {i > 0 && <div className="h-px bg-[var(--border-subtle)] mx-4" />}
              <button
                onClick={() => {
                  action.onClick();
                  onClose();
                }}
                disabled={action.disabled}
                className={`
                  w-full flex items-center gap-3 px-4 min-h-[52px] transition-colors
                  active:bg-[var(--bg-surface-hover)] disabled:opacity-40
                  ${action.variant === 'danger'
                    ? 'text-red-600 dark:text-red-400'
                    : 'text-[var(--text-primary)]'
                  }
                `}
              >
                {action.icon && (
                  <span className="flex-shrink-0 w-6 flex items-center justify-center">
                    {action.icon}
                  </span>
                )}
                <span className="flex flex-col items-start">
                  <span className="text-[15px] font-medium">{action.label}</span>
                  {action.subtitle && (
                    <span className="text-xs text-[var(--text-tertiary)]">{action.subtitle}</span>
                  )}
                </span>
              </button>
            </React.Fragment>
          ))}
        </div>

        {/* Cancel button — separate card (iOS pattern) */}
        <button
          onClick={onClose}
          className="w-full mt-2 bg-[var(--bg-surface-alt)] rounded-ios-card min-h-[52px] text-[15px] font-semibold text-accent-600 dark:text-accent-400 active:bg-[var(--bg-surface-hover)] transition-colors"
        >
          {cancelLabel}
        </button>
      </div>
    </BottomSheet>
  );
}

export default ActionSheet;
